import React, { useMemo, useState } from "react";
import { FiSearch, FiX, FiChevronRight, FiGrid, FiList, FiDollarSign } from "react-icons/fi";
import {
  CardGrid, EntityCard, Group, Row, SearchField, Empty, Section, IconTile, ChipScroll, Chip,
} from "../../activity-logs/styles";
import { SegmentedControl } from "../../activity-logs/components/primitives";
import { Tag, TruncatedText } from "../styles";
import { MoneyValue } from "./parts";
import { TAXES, taxMeta, categoryMeta, formatCount, formatCurrency, formatPercent } from "../dashboardData";

const SORTERS = {
  value: (a, b) => b.valor - a.valor,
  items: (a, b) => b.quantidade - a.quantidade,
  az: (a, b) => a.titulo.localeCompare(b.titulo, "pt-BR"),
};

// Cada linha é um tipo de crédito já somado pela API
export default function CreditList({ creditos, total, tax, setTax, category, setCategory, onOpen }) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("value");
  const [view, setView] = useState("cards");

  const searched = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return creditos;
    return creditos.filter((c) => [c.titulo, categoryMeta(c.categoria).label, c.tipo, c.tipoCredito]
      .some((field) => String(field || "").toLowerCase().includes(term)));
  }, [creditos, q]);

  const taxCounts = useMemo(() => {
    const counts = { all: 0 };
    searched.forEach((c) => {
      if (category && c.categoria !== category) return;
      counts.all += 1;
      counts[c.tipo] = (counts[c.tipo] || 0) + 1;
    });
    return counts;
  }, [searched, category]);

  const categoryCounts = useMemo(() => {
    const counts = {};
    searched.forEach((c) => {
      if (tax !== "all" && c.tipo !== tax) return;
      counts[c.categoria] = (counts[c.categoria] || 0) + 1;
    });
    return counts;
  }, [searched, tax]);

  const filtered = useMemo(
    () => searched
      .filter((c) => (tax === "all" || c.tipo === tax) && (!category || c.categoria === category))
      .sort(SORTERS[sort]),
    [searched, tax, category, sort]
  );

  const share = (c) => formatPercent(total > 0 ? (c.valor / total) * 100 : 0);

  return (
    <Section $first>
      <div data-tour="credit-filters" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <SearchField $max="420px">
          <FiSearch />
          <input
            placeholder="Buscar por tipo de crédito"
            aria-label="Buscar créditos"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {q && <button className="clear" aria-label="Limpar busca" onClick={() => setQ("")}><FiX /></button>}
        </SearchField>
        <div style={{ flex: 1 }} />
        <SegmentedControl
          small
          value={tax}
          onChange={setTax}
          options={[
            { key: "all", label: "Todos", count: taxCounts.all },
            ...TAXES.map((t) => ({ key: t.key, label: t.label, count: taxCounts[t.key] || 0 })),
          ]}
        />
        <SegmentedControl
          small
          value={sort}
          onChange={setSort}
          options={[{ key: "value", label: "Maior valor" }, { key: "items", label: "Mais itens" }, { key: "az", label: "A–Z" }]}
        />
        <SegmentedControl
          small
          value={view}
          onChange={setView}
          options={[{ key: "cards", icon: <FiGrid />, label: "" }, { key: "list", icon: <FiList />, label: "" }]}
        />
      </div>

      <ChipScroll>
        <Chip type="button" $active={!category} onClick={() => setCategory("")}>Todas as categorias</Chip>
        {Object.keys(categoryCounts).map((key) => {
          const meta = categoryMeta(key);
          const Icon = meta.icon;
          return (
            <Chip key={key} type="button" $active={category === key} $color={meta.color} onClick={() => setCategory(category === key ? "" : key)}>
              <Icon /> {meta.label} · {categoryCounts[key]}
            </Chip>
          );
        })}
      </ChipScroll>

      {filtered.length === 0 ? (
        <div style={{ marginTop: 10 }}>
          <Group>
            <Empty>
              <div className="e-icon"><FiDollarSign /></div>
              <div className="e-title">Nenhum crédito encontrado</div>
              <div className="e-sub">{q ? `Nada encontrado para "${q}".` : "Ajuste os filtros para ver outros créditos."}</div>
            </Empty>
          </Group>
        </div>
      ) : view === "cards" ? (
        <CardGrid data-tour="credit-grid" style={{ marginTop: 10 }}>
          {filtered.map((c, idx) => {
            const meta = taxMeta(c.tipo);
            const Icon = categoryMeta(c.categoria).icon;
            return (
              <EntityCard key={c.id} $color={meta.color} $delay={Math.min(idx, 12) * 30} onClick={() => onOpen(c)}>
                <div className="head">
                  <IconTile $size={40} $color={meta.color}><Icon /></IconTile>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="name" title={c.titulo} style={{ whiteSpace: "normal", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{c.titulo}</div>
                    <div className="sub">{categoryMeta(c.categoria).label}</div>
                  </div>
                </div>

                <div style={{ marginTop: 14, fontSize: "1.45em", fontWeight: 600, letterSpacing: "-0.03em" }}>
                  <MoneyValue value={c.valor} />
                </div>

                <div className="foot">
                  <span>{formatCount(c.quantidade)} {c.quantidade === 1 ? "item" : "itens"} · {share(c)}</span>
                  <Tag $color={meta.color}>{meta.label}</Tag>
                </div>
              </EntityCard>
            );
          })}
        </CardGrid>
      ) : (
        <div style={{ marginTop: 10 }}>
          <Group>
            {filtered.map((c) => {
              const meta = taxMeta(c.tipo);
              const Icon = categoryMeta(c.categoria).icon;
              return (
                <Row key={c.id} $clickable $inset="68px" onClick={() => onOpen(c)}>
                  <IconTile $size={38} $color={meta.color}><Icon /></IconTile>
                  <div className="main">
                    <div className="title"><TruncatedText title={c.titulo}>{c.titulo}</TruncatedText></div>
                    <div className="subtitle">{categoryMeta(c.categoria).label} · {formatCount(c.quantidade)} {c.quantidade === 1 ? "item" : "itens"} · {share(c)} do total</div>
                  </div>
                  <div className="trailing">
                    <Tag $color={meta.color}>{meta.label}</Tag>
                    <span className="value" style={{ minWidth: 100, textAlign: "right" }}>{formatCurrency(c.valor)}</span>
                    <FiChevronRight className="chevron" />
                  </div>
                </Row>
              );
            })}
          </Group>
        </div>
      )}
    </Section>
  );
}
